import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  User, FileText, Upload, PenTool, Building2, LogOut,
  CheckCircle, Clock, AlertCircle, Download, Trash2, Eye,
  BookOpen, Shield, Coffee, Laptop, Phone, Mail, MapPin,
  ChevronRight, X, Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pre-joining`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_omni-solutions-1/artifacts/nc5fgtrv_Logo.jpg";

const PreJoiningPortal = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [documents, setDocuments] = useState({ uploaded: [], required: [] });
  const [offerLetter, setOfferLetter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Upload state
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  
  // OTP state
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [debugOtp, setDebugOtp] = useState(null);
  
  // View offer letter
  const [showOfferViewer, setShowOfferViewer] = useState(false);
  
  // Policy acceptance
  const [acceptingPolicy, setAcceptingPolicy] = useState(null);
  
  // Emergency contact form
  const [emergencyContact, setEmergencyContact] = useState({
    name: "",
    relation: "",
    phone: ""
  });
  const [savingEmergencyContact, setSavingEmergencyContact] = useState(false);

  const getToken = () => localStorage.getItem("candidate_token");

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${getToken()}` }
  });

  useEffect(() => {
    const token = getToken();
    if (!token) {
      navigate("/pre-joining/login");
      return;
    }
    fetchAllData();
  }, [navigate]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [profileRes, policiesRes, docsRes, emergencyRes] = await Promise.all([
        axios.get(`${API}/profile`, authHeaders()),
        axios.get(`${API}/policies`, authHeaders()),
        axios.get(`${API}/documents`, authHeaders()),
        axios.get(`${API}/emergency-contact`, authHeaders())
      ]);
      
      setProfile(profileRes.data);
      setPolicies(policiesRes.data);
      setDocuments(docsRes.data);
      
      // Set emergency contact if exists
      if (emergencyRes.data.has_emergency_contact) {
        setEmergencyContact({
          name: emergencyRes.data.emergency_contact_name || "",
          relation: emergencyRes.data.emergency_contact_relation || "",
          phone: emergencyRes.data.emergency_contact_phone || ""
        });
      }
      
      // Try to get offer letter
      try {
        const offerRes = await axios.get(`${API}/offer-letter`, authHeaders());
        setOfferLetter(offerRes.data);
      } catch (e) {
        // Offer letter not yet uploaded
        setOfferLetter(null);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 401) {
        localStorage.removeItem("candidate_token");
        localStorage.removeItem("candidate_data");
        navigate("/pre-joining/login");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("candidate_token");
    localStorage.removeItem("candidate_data");
    toast.success("Logged out successfully");
    navigate("/pre-joining/login");
  };

  const handleFileUpload = async (docType) => {
    if (!selectedFile) {
      toast.error("Please select a file");
      return;
    }

    setUploadingDoc(docType);
    try {
      const formData = new FormData();
      formData.append("document_type", docType);
      formData.append("document", selectedFile);

      await axios.post(`${API}/documents`, formData, {
        ...authHeaders(),
        headers: { ...authHeaders().headers, "Content-Type": "multipart/form-data" }
      });

      toast.success("Document uploaded successfully");
      setSelectedFile(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload document");
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleDeleteDocument = async (docType) => {
    try {
      await axios.delete(`${API}/documents/${docType}`, authHeaders());
      toast.success("Document deleted");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  const handleRequestOtp = async () => {
    setOtpSending(true);
    try {
      const response = await axios.post(`${API}/request-otp`, {}, authHeaders());
      toast.success("OTP sent to your email");
      setShowOtpModal(true);
      // Temporary: Show debug OTP (remove in production)
      if (response.data._debug_otp) {
        setDebugOtp(response.data._debug_otp);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send OTP");
    } finally {
      setOtpSending(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      toast.error("Please enter a valid 6-digit OTP");
      return;
    }

    setOtpVerifying(true);
    try {
      const formData = new FormData();
      formData.append("otp", otp);

      await axios.post(`${API}/verify-otp`, formData, authHeaders());
      toast.success("Offer letter signed successfully!");
      setShowOtpModal(false);
      setOtp("");
      setDebugOtp(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid OTP");
    } finally {
      setOtpVerifying(false);
    }
  };

  const handleAcceptPolicy = async (policyId) => {
    setAcceptingPolicy(policyId);
    try {
      await axios.post(`${API}/policies/${policyId}/accept`, {}, authHeaders());
      toast.success("Policy accepted successfully");
      // Refresh policies to update acceptance status
      const policiesRes = await axios.get(`${API}/policies`, authHeaders());
      setPolicies(policiesRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to accept policy");
    } finally {
      setAcceptingPolicy(null);
    }
  };

  const handleSaveEmergencyContact = async () => {
    if (!emergencyContact.name || !emergencyContact.relation || !emergencyContact.phone) {
      toast.error("Please fill all emergency contact fields");
      return;
    }
    
    setSavingEmergencyContact(true);
    try {
      const formData = new FormData();
      formData.append("name", emergencyContact.name);
      formData.append("relation", emergencyContact.relation);
      formData.append("phone", emergencyContact.phone);
      
      await axios.post(`${API}/emergency-contact`, formData, authHeaders());
      toast.success("Emergency contact saved successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save emergency contact");
    } finally {
      setSavingEmergencyContact(false);
    }
  };

  const handleDownloadSignedOffer = async () => {
    try {
      const response = await axios.get(`${API}/offer-letter/download`, authHeaders());
      const { data, filename, signed_at, candidate_name } = response.data;
      
      // Decode base64 and create download
      const byteCharacters = atob(data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Signed_${filename || 'Offer_Letter.pdf'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Signed offer letter downloaded!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to download offer letter");
    }
  };

  const downloadOfferLetter = () => {
    if (!offerLetter?.data) return;
    
    const link = document.createElement("a");
    link.href = `data:application/pdf;base64,${offerLetter.data}`;
    link.download = offerLetter.filename || "Offer_Letter.pdf";
    link.click();
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-800",
      documents_uploaded: "bg-blue-100 text-blue-800",
      offer_signed: "bg-purple-100 text-purple-800",
      ready_to_join: "bg-green-100 text-green-800"
    };
    return colors[status] || colors.pending;
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: "Documents Pending",
      documents_uploaded: "Documents Uploaded",
      offer_signed: "Offer Signed",
      ready_to_join: "Ready to Join"
    };
    return labels[status] || "Pending";
  };

  const isDocumentUploaded = (docType) => {
    return documents.uploaded?.some(d => d.document_type === docType);
  };

  const getUploadedDoc = (docType) => {
    return documents.uploaded?.find(d => d.document_type === docType);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#1e3a8a] mx-auto mb-4" />
          <p className="text-slate-600">Loading your portal...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="Omnilink Solutions" className="h-10" />
              <div className="hidden sm:block">
                <h1 className="text-lg font-semibold text-slate-900">Pre-Joining Portal</h1>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-slate-900">{profile?.full_name}</p>
                <p className="text-xs text-slate-500">{profile?.designation}</p>
              </div>
              <Button variant="outline" size="sm" onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-[#1e3a8a] to-[#4f46e5] rounded-xl p-6 mb-8 text-white"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold mb-2">Welcome, {profile?.full_name}! 🎉</h2>
              <p className="text-blue-100">
                Complete your pre-joining formalities before your joining date
              </p>
            </div>
            <div className="flex items-center gap-4">
              <Badge className={`${getStatusColor(profile?.status)} text-sm px-4 py-1`}>
                {getStatusLabel(profile?.status)}
              </Badge>
              <div className="text-right">
                <p className="text-blue-200 text-sm">Joining Date</p>
                <p className="text-xl font-bold">{profile?.joining_date}</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Progress Steps */}
        <div className="bg-white rounded-xl p-6 mb-8 border">
          <h3 className="font-semibold text-slate-900 mb-4">Your Progress</h3>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className={`flex-1 p-4 rounded-lg border-2 ${documents.uploaded?.length > 0 ? 'border-green-500 bg-green-50' : 'border-slate-200'}`}>
              <div className="flex items-center gap-3">
                {documents.uploaded?.length > 0 ? (
                  <CheckCircle className="w-6 h-6 text-green-600" />
                ) : (
                  <Clock className="w-6 h-6 text-slate-400" />
                )}
                <div>
                  <p className="font-medium text-slate-900">Upload Documents</p>
                  <p className="text-sm text-slate-500">
                    {documents.uploaded?.length || 0} / {documents.required?.length || 0} uploaded
                  </p>
                </div>
              </div>
            </div>
            <ChevronRight className="w-6 h-6 text-slate-300 hidden sm:block self-center" />
            <div className={`flex-1 p-4 rounded-lg border-2 ${offerLetter ? 'border-green-500 bg-green-50' : 'border-slate-200'}`}>
              <div className="flex items-center gap-3">
                {offerLetter ? (
                  <CheckCircle className="w-6 h-6 text-green-600" />
                ) : (
                  <Clock className="w-6 h-6 text-slate-400" />
                )}
                <div>
                  <p className="font-medium text-slate-900">Review Offer Letter</p>
                  <p className="text-sm text-slate-500">
                    {offerLetter ? 'Available for review' : 'Pending from HR'}
                  </p>
                </div>
              </div>
            </div>
            <ChevronRight className="w-6 h-6 text-slate-300 hidden sm:block self-center" />
            <div className={`flex-1 p-4 rounded-lg border-2 ${profile?.offer_signed ? 'border-green-500 bg-green-50' : 'border-slate-200'}`}>
              <div className="flex items-center gap-3">
                {profile?.offer_signed ? (
                  <CheckCircle className="w-6 h-6 text-green-600" />
                ) : (
                  <Clock className="w-6 h-6 text-slate-400" />
                )}
                <div>
                  <p className="font-medium text-slate-900">Sign Offer Letter</p>
                  <p className="text-sm text-slate-500">
                    {profile?.offer_signed ? `Signed on ${new Date(profile.offer_signed_at).toLocaleDateString()}` : 'E-sign required'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border p-1">
            <TabsTrigger value="overview" className="data-[state=active]:bg-[#1e3a8a] data-[state=active]:text-white" data-testid="tab-overview">
              <User className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="documents" className="data-[state=active]:bg-[#1e3a8a] data-[state=active]:text-white" data-testid="tab-documents">
              <Upload className="w-4 h-4 mr-2" />
              Documents
            </TabsTrigger>
            <TabsTrigger value="offer" className="data-[state=active]:bg-[#1e3a8a] data-[state=active]:text-white" data-testid="tab-offer-letter">
              <PenTool className="w-4 h-4 mr-2" />
              Offer Letter
            </TabsTrigger>
            <TabsTrigger value="policies" className="data-[state=active]:bg-[#1e3a8a] data-[state=active]:text-white" data-testid="tab-policies">
              <BookOpen className="w-4 h-4 mr-2" />
              Policies
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="w-5 h-5 text-[#1e3a8a]" />
                    Your Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">Full Name</p>
                      <p className="font-medium">{profile?.full_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Email</p>
                      <p className="font-medium">{profile?.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Phone</p>
                      <p className="font-medium">{profile?.phone}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Designation</p>
                      <p className="font-medium">{profile?.designation}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Department</p>
                      <p className="font-medium">{profile?.department}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Joining Date</p>
                      <p className="font-medium">{profile?.joining_date}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-[#1e3a8a]" />
                    Company Contact
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                    <div>
                      <p className="font-medium">Office Address</p>
                      <p className="text-sm text-slate-600">1219, 12th Floor, Iconic Shyamal, Shyamal Cross Road, Ahmedabad, Gujarat, IN - 380015</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-slate-400 mt-0.5" />
                    <div>
                      <p className="font-medium">HR Email</p>
                      <p className="text-sm text-slate-600">info@omnilinksolutions.net</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Phone className="w-5 h-5 text-slate-400 mt-0.5" />
                    <div>
                      <p className="font-medium">HR Phone</p>
                      <p className="text-sm text-slate-600">+91-9587064194</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Emergency Contact Form */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Phone className="w-5 h-5 text-[#1e3a8a]" />
                  Emergency Contact
                </CardTitle>
                <CardDescription>
                  Please provide emergency contact details
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="emergency-name">Contact Name *</Label>
                    <Input
                      id="emergency-name"
                      placeholder="Full name"
                      value={emergencyContact.name}
                      onChange={(e) => setEmergencyContact({...emergencyContact, name: e.target.value})}
                      data-testid="emergency-contact-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="emergency-relation">Relation *</Label>
                    <Input
                      id="emergency-relation"
                      placeholder="e.g., Father, Mother, Spouse"
                      value={emergencyContact.relation}
                      onChange={(e) => setEmergencyContact({...emergencyContact, relation: e.target.value})}
                      data-testid="emergency-contact-relation"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="emergency-phone">Phone Number *</Label>
                    <Input
                      id="emergency-phone"
                      placeholder="+91 XXXXXXXXXX"
                      value={emergencyContact.phone}
                      onChange={(e) => setEmergencyContact({...emergencyContact, phone: e.target.value})}
                      data-testid="emergency-contact-phone"
                    />
                  </div>
                </div>
                <Button 
                  className="mt-4 bg-[#1e3a8a] hover:bg-[#172554]" 
                  onClick={handleSaveEmergencyContact}
                  disabled={savingEmergencyContact}
                  data-testid="save-emergency-contact-btn"
                >
                  {savingEmergencyContact ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Save Emergency Contact
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="w-5 h-5 text-[#1e3a8a]" />
                  Upload Required Documents
                </CardTitle>
                <CardDescription>
                  Please upload all required documents. Supported formats: PDF, JPG, PNG (Max 10MB)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {documents.required?.map((doc) => {
                    const uploaded = isDocumentUploaded(doc.type);
                    const uploadedDoc = getUploadedDoc(doc.type);
                    const reviewStatus = uploadedDoc?.review_status;
                    
                    return (
                      <div 
                        key={doc.type}
                        className={`p-4 rounded-lg border-2 ${
                          reviewStatus === 'approved' ? 'border-green-500 bg-green-50' :
                          reviewStatus === 'rejected' ? 'border-red-200 bg-red-50' :
                          uploaded ? 'border-blue-200 bg-blue-50' : 'border-slate-200'
                        }`}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                          <div className="flex items-start gap-3">
                            {reviewStatus === 'approved' ? (
                              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                            ) : reviewStatus === 'rejected' ? (
                              <X className="w-6 h-6 text-red-600 flex-shrink-0" />
                            ) : uploaded ? (
                              <Clock className="w-6 h-6 text-blue-600 flex-shrink-0" />
                            ) : (
                              <AlertCircle className={`w-6 h-6 flex-shrink-0 ${doc.required ? 'text-amber-500' : 'text-slate-400'}`} />
                            )}
                            <div>
                              <p className="font-medium text-slate-900">
                                {doc.label}
                                {doc.required && <span className="text-red-500 ml-1">*</span>}
                              </p>
                              {uploaded && (
                                <div className="mt-1">
                                  <p className="text-sm text-slate-600">
                                    Uploaded: {uploadedDoc?.filename}
                                  </p>
                                  {reviewStatus === 'approved' && (
                                    <Badge className="mt-1 bg-green-100 text-green-800">Approved by HR</Badge>
                                  )}
                                  {reviewStatus === 'rejected' && (
                                    <div>
                                      <Badge className="mt-1 bg-red-100 text-red-800">Rejected</Badge>
                                      {uploadedDoc?.review_remarks && (
                                        <p className="text-sm text-red-600 mt-1">Reason: {uploadedDoc.review_remarks}</p>
                                      )}
                                    </div>
                                  )}
                                  {!reviewStatus && (
                                    <Badge className="mt-1 bg-blue-100 text-blue-800">Pending Review</Badge>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2 ml-9 sm:ml-0">
                            {uploaded && reviewStatus !== 'approved' ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeleteDocument(doc.type)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                {reviewStatus === 'rejected' ? 'Re-upload' : 'Remove'}
                              </Button>
                            ) : !uploaded ? (
                              <>
                                <Input
                                  type="file"
                                  id={`file-${doc.type}`}
                                  className="hidden"
                                  accept=".pdf,.jpg,.jpeg,.png"
                                  onChange={(e) => {
                                    setSelectedFile(e.target.files[0]);
                                    handleFileUpload(doc.type);
                                  }}
                                />
                                <Label
                                  htmlFor={`file-${doc.type}`}
                                  className="cursor-pointer inline-flex items-center px-4 py-2 bg-[#1e3a8a] text-white rounded-md hover:bg-[#172554] text-sm"
                                >
                                  {uploadingDoc === doc.type ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  ) : (
                                    <Upload className="w-4 h-4 mr-2" />
                                  )}
                                  Upload
                                </Label>
                              </>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Offer Letter Tab */}
          <TabsContent value="offer">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PenTool className="w-5 h-5 text-[#1e3a8a]" />
                  Offer Letter
                </CardTitle>
                <CardDescription>
                  Review and e-sign your offer letter
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!offerLetter ? (
                  <div className="text-center py-12 bg-slate-50 rounded-lg">
                    <Clock className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-slate-900 mb-2">Offer Letter Pending</h3>
                    <p className="text-slate-600">
                      Your offer letter is being prepared by HR. Please check back soon.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className={`p-6 rounded-lg border-2 ${offerLetter.signed ? 'border-green-500 bg-green-50' : 'border-blue-200 bg-blue-50'}`}>
                      <div className="flex items-start gap-4">
                        <FileText className={`w-12 h-12 ${offerLetter.signed ? 'text-green-600' : 'text-blue-600'}`} />
                        <div className="flex-grow">
                          <h4 className="font-semibold text-lg">{offerLetter.filename}</h4>
                          <p className={`text-sm ${offerLetter.signed ? 'text-green-700' : 'text-blue-700'}`}>
                            {offerLetter.signed 
                              ? `✓ Digitally Signed on ${new Date(offerLetter.signed_at).toLocaleString()}`
                              : 'Ready for your signature'
                            }
                          </p>
                          {offerLetter.signed && (
                            <Badge className="mt-2 bg-green-100 text-green-800">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Digitally Signed with OTP Verification
                            </Badge>
                          )}
                        </div>
                        <div className="flex gap-2">
                          {offerLetter.signed ? (
                            <Button 
                              variant="outline" 
                              onClick={handleDownloadSignedOffer}
                              data-testid="download-signed-offer-btn"
                            >
                              <Download className="w-4 h-4 mr-2" />
                              Download Signed Copy
                            </Button>
                          ) : (
                            <Button 
                              variant="outline" 
                              onClick={() => setShowOfferViewer(true)}
                              data-testid="view-offer-btn"
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              View Offer Letter
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>

                    {!offerLetter.signed && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                          <div>
                            <h4 className="font-medium text-amber-800">E-Signature Required</h4>
                            <p className="text-sm text-amber-700 mt-1">
                              Please review your offer letter carefully. To sign, we will send an OTP to your registered email for verification.
                            </p>
                            <Button 
                              className="mt-4 bg-[#1e3a8a] hover:bg-[#172554]"
                              onClick={handleRequestOtp}
                              disabled={otpSending}
                              data-testid="sign-offer-btn"
                            >
                              {otpSending ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  Sending OTP...
                                </>
                              ) : (
                                <>
                                  <PenTool className="w-4 h-4 mr-2" />
                                  Sign Offer Letter
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}

                    {offerLetter.signed && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                          <div>
                            <h4 className="font-medium text-green-800">Offer Letter Signed</h4>
                            <p className="text-sm text-green-700 mt-1">
                              You have successfully signed your offer letter. Welcome to Omnilink Solutions!
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Policies Tab */}
          <TabsContent value="policies">
            <Card className="mb-4">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-500" />
                  <p className="text-slate-600">
                    Please read each policy carefully and click &quot;I Accept&quot; to acknowledge that you have read and understood the policy.
                  </p>
                </div>
              </CardContent>
            </Card>
            <div className="space-y-4">
              {policies.map((policy, index) => (
                <motion.div
                  key={policy.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className={policy.accepted ? "border-green-200" : ""}>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${policy.accepted ? 'bg-green-100' : 'bg-[#1e3a8a]/10'}`}>
                            {policy.accepted ? (
                              <CheckCircle className="w-5 h-5 text-green-600" />
                            ) : (
                              <>
                                {policy.category === "general" && <Building2 className="w-5 h-5 text-[#1e3a8a]" />}
                                {policy.category === "code_of_conduct" && <Shield className="w-5 h-5 text-[#1e3a8a]" />}
                                {policy.category === "leave" && <Coffee className="w-5 h-5 text-[#1e3a8a]" />}
                                {policy.category === "it_security" && <Laptop className="w-5 h-5 text-[#1e3a8a]" />}
                                {policy.category === "hr" && <User className="w-5 h-5 text-[#1e3a8a]" />}
                              </>
                            )}
                          </div>
                          <div>
                            <CardTitle className="text-lg">{policy.title}</CardTitle>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="capitalize">
                                {policy.category.replace("_", " ")}
                              </Badge>
                              {policy.accepted && (
                                <Badge className="bg-green-100 text-green-800">
                                  Accepted on {new Date(policy.accepted_at).toLocaleDateString()}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="prose prose-sm max-w-none text-slate-600 whitespace-pre-line mb-4">
                        {policy.content}
                      </div>
                      {!policy.accepted && (
                        <div className="border-t pt-4">
                          <Button
                            onClick={() => handleAcceptPolicy(policy.id)}
                            disabled={acceptingPolicy === policy.id}
                            className="bg-[#1e3a8a] hover:bg-[#172554]"
                            data-testid={`accept-policy-${policy.id}`}
                          >
                            {acceptingPolicy === policy.id ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Accepting...
                              </>
                            ) : (
                              <>
                                <CheckCircle className="w-4 h-4 mr-2" />
                                I Accept This Policy
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* OTP Modal */}
      <Dialog open={showOtpModal} onOpenChange={setShowOtpModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Enter OTP to Sign</DialogTitle>
            <DialogDescription>
              We&apos;ve sent a 6-digit OTP to {profile?.email}. Enter it below to complete your e-signature.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Temporary debug OTP display - remove in production */}
            {debugOtp && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>Debug OTP (for testing):</strong> {debugOtp}
                </p>
                <p className="text-xs text-amber-600 mt-1">
                  This will be removed when email integration is added
                </p>
              </div>
            )}
            <div>
              <Label htmlFor="otp">Enter OTP</Label>
              <Input
                id="otp"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="Enter 6-digit OTP"
                className="text-center text-2xl tracking-[0.5em] font-mono"
                maxLength={6}
                data-testid="otp-input"
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowOtpModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button 
                onClick={handleVerifyOtp} 
                disabled={otpVerifying || otp.length !== 6}
                className="flex-1 bg-[#1e3a8a] hover:bg-[#172554]"
                data-testid="otp-verify-btn"
              >
                {otpVerifying ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  "Verify & Sign"
                )}
              </Button>
            </div>
            <p className="text-center text-sm text-slate-500">
              Didn&apos;t receive OTP?{" "}
              <button 
                onClick={handleRequestOtp}
                className="text-[#1e3a8a] hover:underline"
                disabled={otpSending}
              >
                Resend
              </button>
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Offer Letter Viewer Modal */}
      <Dialog open={showOfferViewer} onOpenChange={setShowOfferViewer}>
        <DialogContent className="max-w-4xl h-[80vh]">
          <DialogHeader>
            <DialogTitle>Offer Letter</DialogTitle>
          </DialogHeader>
          {offerLetter?.data && (
            <iframe
              src={`data:application/pdf;base64,${offerLetter.data}`}
              className="w-full h-full rounded-lg"
              title="Offer Letter"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PreJoiningPortal;
